import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { QRCodeSVG } from 'qrcode.react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Slider from '@mui/material/Slider';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { minisiteGet, minisitePost, minisiteUpload, compressImage } from '../utils/minisiteApi';
import {
  MINISITE_TEXT_FONT_KEYS, resolveMinisiteColor, minisiteFontsHref, useMinisiteTheme,
} from '../utils/minisiteTheme';
import { useOccasion, useBlocLabels, resolveBlocs, hasModule } from '../utils/minisiteOccasions';
import { useT, useFormat, LANGUAGES, IS_MULTILINGUAL, DEFAULT_LANG } from '../i18n';
import { resolveMediaUrl } from '../utils/media';
import MinisiteView from './MinisiteView';
import MinisiteRsvpResponses from './MinisiteRsvpResponses';

// Éditeur des faire-part et invitations animés, ouvert par le lien secret reçu par e-mail après
// l'achat (/personnaliser-mini-site/:slug/:token). La fiche produit ne fixe que le modèle de
// départ : tout se règle ici. La liste des champs enregistrables est bornée côté serveur
// (minisiteSanitizeParams dans apiEtsy.php) : un champ ajouté ici doit l'être aussi là-bas.
// Seuls les modules ACHETÉS sont proposés (hasModule, cf. minisiteModules.json) ; le serveur
// applique la même règle (minisiteStripModules dans apiEtsy.php).

const MAX_ETAPES = 12;
const MAX_DETAILS = 6;
const MAX_QUESTIONS = 3;
// Clés seulement : les libellés (traduits) sont pris dans le dictionnaire au moment de l'affichage.
const PHOTO_FORMES = ['rond', 'arche', 'carre'];
const CADRAGES = ['haut', 'centre', 'bas'];
const PHOTO_EMPLACEMENTS = ['accueil', 'page'];
// Le nom du produit est glissé dans une phrase : en minuscules en français, anglais et espagnol ; tel quel
// dans les autres langues (noms allemands en majuscule, genre de l'article en italien et portugais).
const PRODUIT_EN_MINUSCULES = ['fr', 'en', 'es'];

// Langues proposées pour la page des invités (dans l'ordre), parmi celles qui sont ACTIVES sur le site : l'italien et
// le portugais n'apparaissent donc que le jour où ils sont publiés (REACT_APP_I18N_LANGS).
const PAGE_LANGUAGE_CODES = ['fr', 'en', 'de', 'es', 'it', 'pt'];

// Erreur dont le texte est déjà traduit, prêt à être affiché.
const messageError = (message) => Object.assign(new Error(message), { translated: true });

const Section = ({ title, hint, children }) => (
  <Box sx={{ marginBottom: 3 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
    {hint && <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>{hint}</Typography>}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 1 }}>{children}</Box>
  </Box>
);

const Choice = ({ label, value, options, onChange }) => (
  <Box>
    <Typography variant="body2" sx={{ marginBottom: 0.5 }}>{label}</Typography>
    <ToggleButtonGroup exclusive size="small" value={value} onChange={(_, v) => v && onChange(v)}>
      {options.map((o) => <ToggleButton key={o.key} value={o.key}>{o.label}</ToggleButton>)}
    </ToggleButtonGroup>
  </Box>
);

const MinisiteEditor = () => {
  const { slug, token } = useParams();
  const t = useT('minisite-editor');
  const fmt = useFormat();
  const [form, setForm] = useState(null);
  const [savedJson, setSavedJson] = useState('');
  const [expireLe, setExpireLe] = useState('');
  const [error, setError] = useState('');   // clé du message (errors.*), traduite à l'affichage
  const [tab, setTab] = useState('textes');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const [replayKey, setReplayKey] = useState(0);
  const [responseCount, setResponseCount] = useState(null);
  const photoInput = useRef(null);
  const fondInput = useRef(null);
  // Vocabulaire de l'occasion, couleurs, animations et libellés de sections dans la langue de l'acheteur (lot C).
  const occasion = useOccasion(form);
  const theme = useMinisiteTheme();
  const blocLabels = useBlocLabels();

  useEffect(() => {
    minisiteGet('getMinisite', { slug, token })
      .then((data) => {
        if (!data?.success) return setError('notFound');
        if (!data.data.can_edit) return setError('invalidEditLink');
        setForm(data.data.params || {});
        setSavedJson(JSON.stringify(data.data.params || {}));
        return setExpireLe(data.data.en_ligne_jusqu_au || '');
      })
      .catch(() => setError('loadFailed'));
  }, [slug, token]);

  const dirty = form !== null && JSON.stringify(form) !== savedJson;

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const field = (key) => ({ value: form[key] ?? '', onChange: (e) => set(key, e.target.value), fullWidth: true });
  const toggle = (key, defaultOn = false) => ({
    checked: form[key] === undefined ? defaultOn : !!form[key],
    onChange: (e) => set(key, e.target.checked),
  });
  const handleResponseCount = useCallback((n) => setResponseCount(n), []);

  const applyServerParams = (params) => {
    setForm(params);
    setSavedJson(JSON.stringify(params));
  };

  // Message du serveur : traduit d'après son code `erreur` s'il en envoie un (en français, ou si le code est
  // inconnu, c'est le message français du serveur qui s'affiche) ; jamais de code brut à l'écran.
  const serverMessage = (data, fallback) => {
    const code = typeof data?.erreur === 'string' ? data.erreur.replace(/[^a-z_]/g, '') : '';
    const known = code ? t.or(`minisite-editor.errors.${code}`, '') : '';
    return known || (typeof data?.message === 'string' && data.message) || fallback;
  };
  // Texte à afficher pour une erreur attrapée (réponse du serveur, image illisible, panne réseau).
  const failureText = (e, fallbackKey) => {
    if (e?.translated) return e.message;
    if (e?.code === 'compression' || e?.code === 'image_illisible') return t(`minisite-editor.errors.${e.code}`);
    return t(`minisite-editor.errors.${fallbackKey}`);
  };

  const save = (quiet = false) => {
    setSaving(true);
    return minisitePost('updateMinisiteParams', { slug, token, params: form })
      .then((data) => {
        if (!data?.success) throw messageError(serverMessage(data, t('minisite-editor.errors.save')));
        applyServerParams(data.params);
        if (!quiet) toast.success(t('minisite-editor.toasts.saved'));
        return data.params;
      })
      .catch((e) => { const shown = failureText(e, 'save'); toast.error(shown); throw messageError(shown); })
      .finally(() => setSaving(false));
  };

  // Les réglages en cours sont enregistrés AVANT l'envoi : le serveur renvoie ensuite la page
  // complète, image comprise, et rien de ce qui a été saisi n'est perdu.
  const handleUpload = async (emplacement, file) => {
    if (!file) return;
    setUploading(emplacement);
    try {
      if (dirty) await save(true);
      const blob = await compressImage(file, emplacement === 'fond' ? 1800 : 1200);
      const data = await minisiteUpload({ slug, token, emplacement }, blob);
      if (!data?.success) throw messageError(serverMessage(data, t('minisite-editor.errors.upload')));
      applyServerParams(data.params);
      toast.success(emplacement === 'fond' ? t('minisite-editor.toasts.backgroundSaved') : t('minisite-editor.toasts.photoSaved'));
    } catch (e) {
      toast.error(failureText(e, 'upload'));
    } finally {
      setUploading('');
    }
  };

  const handleDeleteImage = async (emplacement) => {
    try {
      if (dirty) await save(true);
      const data = await minisitePost('deleteMinisiteImage', { slug, token, emplacement });
      if (data?.success) applyServerParams(data.params);
    } catch (e) { /* message déjà affiché par save() */ }
  };

  if (error) return <Typography variant="h6" sx={{ textAlign: 'center', padding: 6 }}>{t(`minisite-editor.errors.${error}`)}</Typography>;
  if (!form) return <Typography sx={{ textAlign: 'center', padding: 6 }}>{t('minisite-editor.loading')}</Typography>;

  const shareUrl = `${window.location.origin}/i/${slug}`;
  const titre = t('minisite-editor.title', { produit: PRODUIT_EN_MINUSCULES.includes(t.lang) ? occasion.produit.toLowerCase() : occasion.produit });
  // Langue de la page pour les invités : le réglage enregistré, sinon le français.
  const pageLanguages = LANGUAGES.filter((l) => PAGE_LANGUAGE_CODES.includes(l.code));
  const pageLang = pageLanguages.some((l) => l.code === form.langue) ? form.langue : DEFAULT_LANG;
  // Réglage pas encore choisi et éditeur ouvert dans une autre langue que le français : on propose cette langue
  // (un clic ; rien n'est enregistré à la place de l'acheteur).
  const editorLanguage = t.lang !== DEFAULT_LANG ? pageLanguages.find((l) => l.code === t.lang) : null;
  const accent = resolveMinisiteColor(form.couleur);
  const isCustomColor = /^#[0-9a-fA-F]{6}$/.test(form.couleur || '');
  const blocs = resolveBlocs(form);
  const has = (key) => hasModule(form, key);
  const tabs = [
    { key: 'textes', label: t('minisite-editor.tabs.texts') },
    { key: 'style', label: t('minisite-editor.tabs.style') },
    (has('photo') || has('fond_perso')) && { key: 'images', label: has('photo') && has('fond_perso') ? t('minisite-editor.tabs.photoAndBackground') : has('photo') ? t('minisite-editor.tabs.photo') : t('minisite-editor.tabs.background') },
    has('programme') && { key: 'programme', label: t('minisite-editor.tabs.program') },
    (has('lieu') || has('cagnotte')) && { key: 'lieu', label: has('lieu') && has('cagnotte') ? t('minisite-editor.tabs.placeAndFund') : has('lieu') ? t('minisite-editor.tabs.place') : t('minisite-editor.tabs.fund') },
    has('rsvp') && { key: 'reponses', label: responseCount !== null ? t('minisite-editor.tabs.answersCount', { n: responseCount }) : t('minisite-editor.tabs.answers') },
    { key: 'partage', label: t('minisite-editor.tabs.share') },
  ].filter(Boolean);

  // Listes éditables (programme, informations, questions) : mêmes gestes partout.
  const listOf = (key) => (Array.isArray(form[key]) ? form[key] : []);
  const setItem = (key, index, value) => set(key, listOf(key).map((item, i) => (i === index ? value : item)));
  const removeItem = (key, index) => set(key, listOf(key).filter((_, i) => i !== index));
  const moveItem = (key, list, index, delta) => {
    const next = [...list];
    [next[index], next[index + delta]] = [next[index + delta], next[index]];
    set(key, next);
  };
  const moveButtons = (listKey, list, index) => (
    <>
      <IconButton size="small" aria-label={t('minisite-editor.buttons.moveUp')} disabled={index === 0} onClick={() => moveItem(listKey, list, index, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
      <IconButton size="small" aria-label={t('minisite-editor.buttons.moveDown')} disabled={index === list.length - 1} onClick={() => moveItem(listKey, list, index, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
    </>
  );

  const imageSlot = (emplacement, url, inputRef) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      {url && (
        <Box component="img" src={resolveMediaUrl(url)} alt="" sx={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 2, border: '1px solid rgba(0,0,0,0.15)' }} />
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(e) => { handleUpload(emplacement, e.target.files?.[0]); e.target.value = ''; }}
      />
      <Button variant="outlined" disabled={!!uploading} onClick={() => inputRef.current?.click()}>
        {uploading === emplacement ? t('minisite-editor.images.uploading') : t(`minisite-editor.images.${emplacement === 'fond' ? 'background' : 'photo'}.${url ? 'replace' : 'choose'}`)}
      </Button>
      {url && <Button color="inherit" disabled={!!uploading} onClick={() => handleDeleteImage(emplacement)}>{t('minisite-editor.images.remove')}</Button>}
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1100, margin: '0 auto', padding: { xs: 1, sm: 3 } }}>
      <Helmet>
        <title>{`${titre} — Joli Ptit Mot`}</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="stylesheet" href={minisiteFontsHref()} />
      </Helmet>
      <ToastContainer position="top-right" autoClose={3000} />
      <Typography variant="h5" gutterBottom>{titre}</Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
            <div className="ms-phone">
              <MinisiteView key={replayKey} embedded params={form} slug={slug} langue={form.langue || 'fr'} />
            </div>
            <Box sx={{ textAlign: 'center', marginTop: 1 }}>
              <Button size="small" onClick={() => setReplayKey((k) => k + 1)}>{t('minisite-editor.replay')}</Button>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ marginBottom: 3 }}>
            {tabs.map((item) => <Tab key={item.key} value={item.key} label={item.label} />)}
          </Tabs>

          {tab === 'textes' && (
            <>
              <Section title={t('minisite-editor.texts.welcome.title')}>
                <TextField label={t('minisite-editor.texts.welcome.intro')} placeholder={occasion.intro} inputProps={{ maxLength: 60 }} {...field('intro')} />
                <TextField label={occasion.nomsLabel} placeholder={occasion.nomsPlaceholder} inputProps={{ maxLength: 80 }} {...field('prenoms')} />
                <TextField label={t('minisite-editor.texts.welcome.subtitle')} placeholder={occasion.sousTitrePlaceholder || t('minisite-editor.texts.welcome.subtitleExample')} inputProps={{ maxLength: 80 }} {...field('sous_titre')} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label={occasion.dateLabel} type="date" InputLabelProps={{ shrink: true }} {...field('date')} />
                  <TextField label={t('minisite-editor.texts.welcome.time')} type="time" InputLabelProps={{ shrink: true }} {...field('heure')} />
                </Box>
                {occasion.compteARebours && has('compte_a_rebours') && (
                  <FormControlLabel control={<Switch {...toggle('compte_a_rebours', true)} />} label={t('minisite-editor.texts.welcome.countdown')} />
                )}
              </Section>
              {has('texte') && (
              <Section title={t('minisite-editor.texts.message.title')} hint={t('minisite-editor.texts.message.hint')}>
                <TextField
                  label={t('minisite-editor.texts.message.text')}
                  placeholder={occasion.texteExemple}
                  multiline
                  rows={6}
                  inputProps={{ maxLength: 1200 }}
                  helperText={t('minisite-editor.texts.message.counter', { n: (form.texte || '').length })}
                  {...field('texte')}
                />
                <TextField label={t('minisite-editor.texts.message.signature')} placeholder="Marie & Thomas" inputProps={{ maxLength: 80 }} {...field('signature')} />
              </Section>
              )}
              {has('details') && (
              <Section title={t('minisite-editor.texts.details.title')} hint={t('minisite-editor.texts.details.hint')}>
                <TextField label={t('minisite-editor.texts.details.sectionTitle')} inputProps={{ maxLength: 60 }} {...field('details_titre')} />
                {listOf('details').map((ligne, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField label={t('minisite-editor.texts.details.label')} placeholder={t('minisite-editor.texts.details.labelExample')} value={ligne.label || ''} onChange={(e) => setItem('details', index, { ...ligne, label: e.target.value })} inputProps={{ maxLength: 40 }} sx={{ width: 170 }} />
                    <TextField label={t('minisite-editor.texts.details.value')} placeholder={t('minisite-editor.texts.details.valueExample')} value={ligne.valeur || ''} onChange={(e) => setItem('details', index, { ...ligne, valeur: e.target.value })} inputProps={{ maxLength: 80 }} fullWidth />
                    {moveButtons('details', listOf('details'), index)}
                    <IconButton size="small" aria-label={t('minisite-editor.buttons.remove')} onClick={() => removeItem('details', index)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </Box>
                ))}
                <Box>
                  <Button startIcon={<AddIcon />} disabled={listOf('details').length >= MAX_DETAILS} onClick={() => set('details', [...listOf('details'), { label: '', valeur: '' }])}>
                    {t('minisite-editor.texts.details.add')}
                  </Button>
                </Box>
              </Section>
              )}
            </>
          )}

          {tab === 'style' && (
            <>
              <Section title={t('minisite-editor.style.fonts.title')}>
                <Grid container spacing={1}>
                  {theme.fonts.map((font) => (
                    <Grid item xs={6} sm={4} key={font.key}>
                      <Paper
                        variant="outlined"
                        onClick={() => set('police', font.key)}
                        sx={{
                          cursor: 'pointer', padding: 1.5, textAlign: 'center',
                          borderColor: (form.police || 'parisienne') === font.key ? accent : undefined,
                          borderWidth: (form.police || 'parisienne') === font.key ? 2 : 1,
                        }}
                      >
                        <Box sx={{ fontFamily: font.family, fontSize: '1.5rem', lineHeight: 1.3, color: accent }}>
                          {(form.prenoms || occasion.nomsPlaceholder).slice(0, 18)}
                        </Box>
                        <Typography variant="caption" color="text.secondary">{font.label}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                <TextField select label={t('minisite-editor.style.fonts.text')} {...field('police_texte')} value={form.police_texte || 'dm-sans'}>
                  {theme.fonts.filter((f) => MINISITE_TEXT_FONT_KEYS.includes(f.key)).map((f) => (
                    <MenuItem key={f.key} value={f.key} sx={{ fontFamily: f.family }}>{f.label}</MenuItem>
                  ))}
                </TextField>
                <Box>
                  <Typography variant="body2">{t('minisite-editor.style.fonts.size')}</Typography>
                  <Slider min={60} max={160} step={5} value={Number(form.taille_prenoms) || 100} onChange={(_, v) => set('taille_prenoms', v)} valueLabelDisplay="auto" valueLabelFormat={(v) => t('minisite-editor.style.percent', { value: v })} />
                </Box>
              </Section>

              <Section title={t('minisite-editor.style.color.title')} hint={t('minisite-editor.style.color.hint')}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  {theme.colors.map((color) => (
                    <Box
                      key={color.key}
                      role="button"
                      aria-label={color.label}
                      title={color.label}
                      onClick={() => set('couleur', color.key)}
                      sx={{
                        width: 34, height: 34, borderRadius: '50%', cursor: 'pointer', backgroundColor: color.hex,
                        border: '1px solid rgba(0,0,0,0.2)',
                        outline: form.couleur === color.key ? `3px solid ${color.hex}` : 'none', outlineOffset: 2,
                      }}
                    />
                  ))}
                  <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1, marginLeft: 1, cursor: 'pointer' }}>
                    <input
                      type="color"
                      value={accent}
                      onChange={(e) => set('couleur', e.target.value)}
                      style={{ width: 40, height: 34, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: isCustomColor ? 600 : 400 }}>
                      {isCustomColor ? t('minisite-editor.style.color.custom', { hex: form.couleur }) : t('minisite-editor.style.color.other')}
                    </Typography>
                  </Box>
                </Box>
              </Section>

              <Section title={t('minisite-editor.style.layout.title')} hint={t('minisite-editor.style.layout.hint')}>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Choice label={t('minisite-editor.style.layout.position')} value={form.disposition || 'centre'} options={theme.dispositions} onChange={(v) => set('disposition', v)} />
                  <Choice label={t('minisite-editor.style.layout.ambiance')} value={form.ambiance || 'claire'} options={theme.ambiances} onChange={(v) => set('ambiance', v)} />
                </Box>
                <Box>
                  <Typography variant="body2">{t('minisite-editor.style.layout.veil')}</Typography>
                  <Slider min={0} max={100} step={5} value={form.voile === undefined ? 70 : Number(form.voile)} onChange={(_, v) => set('voile', v)} valueLabelDisplay="auto" />
                </Box>
              </Section>

              <Section title={t('minisite-editor.style.animation.title')}>
                <TextField
                  select
                  {...field('animation')}
                  value={form.animation || 'douce'}
                  onChange={(e) => { set('animation', e.target.value); setReplayKey((k) => k + 1); }}
                >
                  {theme.animations.map((a) => <MenuItem key={a.key} value={a.key}>{a.label}</MenuItem>)}
                </TextField>
              </Section>
            </>
          )}

          {tab === 'images' && (
            <>
              {has('photo') && (
              <Section title={t('minisite-editor.images.photo.title')} hint={t('minisite-editor.images.photo.hint')}>
                {imageSlot('photo', form.photo, photoInput)}
                {form.photo && (
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Choice label={t('minisite-editor.images.photo.shape')} value={form.photo_forme || 'rond'} options={PHOTO_FORMES.map((k) => ({ key: k, label: t(`minisite-editor.images.shapes.${k}`) }))} onChange={(v) => set('photo_forme', v)} />
                    <Choice
                      label={t('minisite-editor.images.photo.placement')}
                      value={form.photo_emplacement || 'accueil'}
                      options={PHOTO_EMPLACEMENTS.map((k) => ({ key: k, label: t(`minisite-editor.images.placements.${k}`) }))}
                      onChange={(v) => set('photo_emplacement', v)}
                    />
                  </Box>
                )}
              </Section>
              )}
              {has('fond_perso') && (
              <Section
                title={t('minisite-editor.images.background.title')}
                hint={t('minisite-editor.images.background.hint')}
              >
                {imageSlot('fond', form.fond_perso, fondInput)}
                {form.fond_perso && (
                  <>
                    <FormControlLabel control={<Switch {...toggle('fond_perso_actif', true)} />} label={t('minisite-editor.images.background.use')} />
                    <Choice label={t('minisite-editor.images.background.framing')} value={form.fond_cadrage || 'centre'} options={CADRAGES.map((k) => ({ key: k, label: t(`minisite-editor.images.framings.${k}`) }))} onChange={(v) => set('fond_cadrage', v)} />
                    <Typography variant="body2" color="text.secondary">
                      {t('minisite-editor.images.background.readability', { tab: t('minisite-editor.tabs.style') })}
                    </Typography>
                  </>
                )}
                <Typography variant="caption" color="text.secondary">
                  {t('minisite-editor.images.background.rights')}
                </Typography>
              </Section>
              )}
            </>
          )}

          {tab === 'programme' && (
            <Section title={t('minisite-editor.program.title')} hint={t('minisite-editor.program.hint')}>
              <TextField label={t('minisite-editor.sectionTitle')} placeholder={t('minisite-editor.program.titleExample')} inputProps={{ maxLength: 60 }} {...field('programme_titre')} />
              {listOf('programme').map((etape, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField label={t('minisite-editor.program.time')} placeholder={t('minisite-editor.program.timeExample')} value={etape.heure || ''} onChange={(e) => setItem('programme', index, { ...etape, heure: e.target.value })} inputProps={{ maxLength: 10 }} sx={{ width: 110 }} />
                  <TextField label={t('minisite-editor.program.step')} placeholder={t('minisite-editor.program.stepExample')} value={etape.titre || ''} onChange={(e) => setItem('programme', index, { ...etape, titre: e.target.value })} inputProps={{ maxLength: 80 }} fullWidth />
                  {moveButtons('programme', listOf('programme'), index)}
                  <IconButton size="small" aria-label={t('minisite-editor.program.removeStep')} onClick={() => removeItem('programme', index)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </Box>
              ))}
              <Box>
                <Button startIcon={<AddIcon />} disabled={listOf('programme').length >= MAX_ETAPES} onClick={() => set('programme', [...listOf('programme'), { heure: '', titre: '' }])}>
                  {t('minisite-editor.program.add')}
                </Button>
              </Box>
            </Section>
          )}

          {tab === 'lieu' && (
            <>
              {has('lieu') && (
              <Section title={t('minisite-editor.place.title')}>
                <TextField label={t('minisite-editor.place.name')} placeholder="Domaine des Roses" inputProps={{ maxLength: 160 }} {...field('lieu')} />
                <TextField label={t('minisite-editor.place.address')} placeholder="1200 route de Puyricard, 13100 Aix-en-Provence" inputProps={{ maxLength: 200 }} {...field('adresse')} />
                <TextField
                  label={t('minisite-editor.place.maps')}
                  placeholder={t('minisite-editor.place.mapsExample')}
                  helperText={t('minisite-editor.place.mapsHint')}
                  inputProps={{ maxLength: 300 }}
                  {...field('maps')}
                />
                <FormControlLabel control={<Switch {...toggle('carte_lieu', true)} />} label={t('minisite-editor.place.showMap')} />
              </Section>
              )}
              {has('cagnotte') && (
              <Section title={t('minisite-editor.fund.title')} hint={t('minisite-editor.fund.hint')}>
                <TextField
                  label={t('minisite-editor.fund.link')}
                  placeholder="https://…"
                  error={!!form.cagnotte_url && !/^https:\/\//.test(form.cagnotte_url)}
                  helperText={form.cagnotte_url && !/^https:\/\//.test(form.cagnotte_url) ? t('minisite-editor.fund.linkError') : ' '}
                  inputProps={{ maxLength: 300 }}
                  {...field('cagnotte_url')}
                />
                <TextField label={t('minisite-editor.sectionTitle')} placeholder={t('minisite-editor.fund.titleExample')} inputProps={{ maxLength: 60 }} {...field('cagnotte_titre')} />
                <TextField label={t('minisite-editor.fund.words')} placeholder={t('minisite-editor.fund.wordsExample')} multiline rows={2} inputProps={{ maxLength: 300 }} {...field('cagnotte_texte')} />
              </Section>
              )}
            </>
          )}

          {tab === 'reponses' && (
            <>
              <Section title={t('minisite-editor.answers.online.title')} hint={t('minisite-editor.answers.online.hint')}>
                <FormControlLabel control={<Switch {...toggle('rsvp_actif')} />} label={t('minisite-editor.answers.online.enable')} />
                {form.rsvp_actif && (
                  <>
                    <TextField label={t('minisite-editor.sectionTitle')} placeholder={t('minisite-editor.answers.online.titleExample')} inputProps={{ maxLength: 60 }} {...field('rsvp_titre')} />
                    <TextField label={t('minisite-editor.answers.online.message')} placeholder={t('minisite-editor.answers.online.messageExample')} multiline rows={2} inputProps={{ maxLength: 400 }} {...field('rsvp_message')} />
                    <TextField label={t('minisite-editor.answers.online.deadline')} type="date" InputLabelProps={{ shrink: true }} helperText={t('minisite-editor.answers.online.deadlineHint')} {...field('rsvp_date_limite')} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('minisite-editor.answers.online.questionsTitle')}</Typography>
                    <FormControlLabel control={<Switch {...toggle('rsvp_accompagnants')} />} label={t('minisite-editor.answers.online.adults')} />
                    <FormControlLabel control={<Switch {...toggle('rsvp_enfants')} />} label={t('minisite-editor.answers.online.children')} />
                    {has('rsvp_avance') && <FormControlLabel control={<Switch {...toggle('rsvp_repas')} />} label={t('minisite-editor.answers.online.meal')} />}
                    {has('rsvp_avance') && form.rsvp_repas && (
                      <TextField label={t('minisite-editor.answers.online.mealOptions')} placeholder={t('minisite-editor.answers.online.mealOptionsExample')} helperText={t('minisite-editor.answers.online.mealOptionsHint')} inputProps={{ maxLength: 300 }} {...field('rsvp_repas_options')} />
                    )}
                    <FormControlLabel control={<Switch {...toggle('rsvp_allergies')} />} label={t('minisite-editor.answers.online.allergies')} />
                    <FormControlLabel control={<Switch {...toggle('rsvp_message_invite', true)} />} label={t('minisite-editor.answers.online.note')} />
                    {has('rsvp_avance') && <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('minisite-editor.answers.online.ownQuestions')}</Typography>}
                    {has('rsvp_avance') && listOf('rsvp_questions').map((question, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField label={t('minisite-editor.answers.online.question', { n: index + 1 })} placeholder={t('minisite-editor.answers.online.questionExample')} value={question} onChange={(e) => setItem('rsvp_questions', index, e.target.value)} inputProps={{ maxLength: 120 }} fullWidth />
                        <IconButton size="small" aria-label={t('minisite-editor.answers.online.removeQuestion')} onClick={() => removeItem('rsvp_questions', index)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </Box>
                    ))}
                    {has('rsvp_avance') && (
                    <Box>
                      <Button startIcon={<AddIcon />} disabled={listOf('rsvp_questions').length >= MAX_QUESTIONS} onClick={() => set('rsvp_questions', [...listOf('rsvp_questions'), ''])}>
                        {t('minisite-editor.answers.online.addQuestion')}
                      </Button>
                    </Box>
                    )}
                  </>
                )}
              </Section>
              {form.rsvp_actif && (
                <Section title={t('minisite-editor.answers.received.title')}>
                  <MinisiteRsvpResponses slug={slug} token={token} onCount={handleResponseCount} />
                </Section>
              )}
            </>
          )}

          {tab === 'partage' && (
            <>
              <Section title={t('minisite-editor.share.order.title')} hint={t('minisite-editor.share.order.hint')}>
                {blocs.map((key, index) => (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ flex: 1 }}>{index + 1}. {blocLabels[key]}</Typography>
                    {moveButtons('blocs', blocs, index)}
                  </Box>
                ))}
              </Section>
              {IS_MULTILINGUAL && (
              <Section title={t('minisite-editor.share.language.title')} hint={t('minisite-editor.share.language.hint')}>
                <TextField select label={t('minisite-editor.share.language.label')} value={pageLang} onChange={(e) => set('langue', e.target.value)} fullWidth>
                  {pageLanguages.map((l) => <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>)}
                </TextField>
                {!form.langue && editorLanguage && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">{t('minisite-editor.share.language.unset')}</Typography>
                    <Button size="small" onClick={() => set('langue', editorLanguage.code)}>
                      {t('minisite-editor.share.language.useEditor', { language: editorLanguage.label })}
                    </Button>
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary">{t('minisite-editor.share.language.note')}</Typography>
              </Section>
              )}
              <Section title={t('minisite-editor.share.link.title')} hint={t('minisite-editor.share.link.hint')}>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ padding: 1, backgroundColor: '#fff', borderRadius: 1, lineHeight: 0 }}>
                    <QRCodeSVG value={shareUrl} size={132} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      <a href={shareUrl} target="_blank" rel="noreferrer">{shareUrl}</a>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
                      {t('minisite-editor.share.link.qr')}
                    </Typography>
                    {expireLe && (
                      <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
                        {t.rich('minisite-editor.share.link.online', { date: fmt.date(expireLe, 'long') })}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Section>
            </>
          )}

          <Paper
            elevation={3}
            sx={{ position: 'sticky', bottom: 12, padding: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', marginTop: 2, zIndex: 2 }}
          >
            <Button variant="contained" color="primary" onClick={() => save().catch(() => {})} disabled={saving || !dirty}>
              {saving ? t('minisite-editor.buttons.saving') : dirty ? t('minisite-editor.buttons.save') : t('minisite-editor.buttons.saved')}
            </Button>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="caption" color="text.secondary">{t('minisite-editor.share.link.label')}</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                <a href={shareUrl} target="_blank" rel="noreferrer">{shareUrl}</a>
              </Typography>
            </Box>
            <Button size="small" onClick={() => navigator.clipboard?.writeText(shareUrl).then(() => toast.info(t('minisite-editor.toasts.linkCopied')))}>
              {t('minisite-editor.buttons.copy')}
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MinisiteEditor;
